<template>
  <div>
    <v-dialog :modelValue="showDialog" persistent maxWidth="750">
      <v-card tag="form" @submit.prevent="processSubmit">
        <v-card-title class="headline"
          >{{ isUpdating ? "Update" : "Add new" }} Tracker</v-card-title
        >
        <v-card-text>
          <v-form ref="trackerPageForm">
            <div class="input-wrapper">
              <RfTextInput
                v-model="custom_field.name"
                variant="outlined"
                density="compact"
                class="grow-1"
                placeholder="Your tracker's name"
                title="Name"
                :disabled="!!disabled"
              />
              <RfTextInput
                v-model="custom_field.label"
                variant="outlined"
                density="compact"
                class="grow-1"
                title="Label"
                :disabled="isUpdating || !!disabled"
                placeholder="Tracker's label"
              />
            </div>
            <RfTextInput
              v-model="custom_field.description"
              variant="outlined"
              density="compact"
              placeholder="Tracker's description"
              title="Description"
              :disabled="!!disabled"
              textarea
              noResize
              rows="3"
            />
            <div class="no-value setting-add-tracker-type">
              <h3>Tracker type</h3>
              <v-radio-group
                v-model="custom_field.data_type"
                :disabled="!!disabled"
                inline
                hideDetails
                style="margin-top: 0"
              >
                <v-radio
                  v-for="n in Object.keys(dataTypes)"
                  :key="n"
                  :label="n"
                  :value="n"
                ></v-radio>
              </v-radio-group>

              <RfTextInput
                v-if="isRedfastConnector"
                v-show="custom_field.data_type === 'track'"
                v-model="custom_field.values[0]"
                variant="outlined"
                density="compact"
                :disabled="!!disabled"
                placeholder="Value"
                hideDetails
                bgColor="#ffffff"
              />
              <div v-if="isPageTracker">
                <RfTextInput
                  v-model="pageSettings.url_path"
                  variant="outlined"
                  density="compact"
                  validateOnBlur
                  :disabled="!!disabled"
                  title="Url Path"
                  :rules="[isValidUrlRule]"
                  bgColor="#ffffff"
                  placeholder="/settings"
                  @blur="validateTrackerForm"
                />
                <RfTextInput
                  v-model.trim="pageSettings.query_params"
                  variant="outlined"
                  density="compact"
                  validateOnBlur
                  :disabled="!!disabled"
                  title="Query Parameters"
                  :rules="[isValidUrlRule]"
                  bgColor="#ffffff"
                  placeholder="version=2020"
                  @blur="validateTrackerForm"
                />
                <RfTextInput
                  v-model.trim="pageSettings.url_hash"
                  variant="outlined"
                  density="compact"
                  validateOnBlur
                  :disabled="!!disabled"
                  title="Url Hash"
                  :rules="[isValidUrlRule]"
                  bgColor="#ffffff"
                  placeholder="#profile"
                  @blur="validateTrackerForm"
                />
              </div>
              <div v-if="regexAllowed" class="use-reg-expression-opt">
                <div class="flex items-center">
                  <div class="use-reg-title">Match by:</div>
                  <v-radio-group
                    v-model.trim="custom_field.options.use_regex"
                    hideDetails
                    class="m-1 mt-1 ml-4 pt-0"
                    :disabled="!!disabled"
                    inline
                    @update:modelValue="validateTrackerForm"
                  >
                    <v-radio :value="false" label="Wildcards (*)" />
                    <v-radio :value="true" label="Regular Expression" />
                  </v-radio-group>
                </div>
                <button
                  type="button"
                  class="use-reg-test-btn"
                  :disabled="disabled || !containsRegexOrWildcards || !isTrackerFormValid"
                  @click.stop="regexTester?.open()"
                >
                  {{ testerLabel }}
                </button>
                <RfRegexTester
                  v-if="!disabled && containsRegexOrWildcards && isTrackerFormValid"
                  ref="regexTester"
                  :testType="testType"
                  :urlPath="pageSettings.url_path"
                  :urlHash="pageSettings.url_hash"
                  :queryParams="pageSettings.query_params"
                  :useRegex="custom_field.options.use_regex"
                />
              </div>
              <div v-if="custom_field.data_type === 'advanced'" class="mt-6">
                <RfSelect
                  v-model="custom_field.options.custom_trigger_id"
                  variant="outlined"
                  density="compact"
                  :disabled="!!disabled"
                  title="Advanced"
                  itemTitle="text"
                  :items="customTriggers"
                />
                <RfTextInput
                  v-model="custom_field.options.custom_trigger_seconds"
                  variant="outlined"
                  :disabled="!!disabled"
                  density="compact"
                  hideDetails
                  type="number"
                  title="Advanced Interval (seconds)"
                />
                <RfAdvancedArguments ref="advancedArgsBody" />
              </div>
            </div>
            <v-checkbox
              v-if="isPageTracker && currApp.flags?.paywalls"
              v-model="custom_field.options.paywall"
              :disabled="!!disabled"
              hideDetails
              class="mt-2"
            >
              <template #label>
                <span>Use as paywall tracker</span>
                <v-tooltip location="top" maxWidth="350">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="18" color="#888" class="ml-1">md:info</v-icon>
                  </template>
                  <span>
                    When enabled, this will track visits to trigger paywall prompts. Select this
                    tracker in the paywall prompt's design settings to configure
                  </span>
                </v-tooltip>
              </template>
            </v-checkbox>
            <v-checkbox
              v-if="canNormalize"
              v-model="custom_field.is_normalized"
              :disabled="!!disabled"
              label="Normalize data into 0-10 scale"
              style="margin-top: 0"
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-5!" variant="outlined" @click="$emit('resetForm')">
            {{ !disabled ? "Cancel" : "Close" }}
          </v-btn>
          <RfButton
            :icon="isUpdating ? 'md:update' : 'md:add'"
            :buttonText="isUpdating ? 'Update Usage Tracking' : 'Add Usage Tracking'"
            :color="isUpdating ? 'success' : 'accent'"
            type="submit"
            class="px-5!"
            :disabled="disabled"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RfRegexTester from "@/components/RfCommonCards/RfRegexTester.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import { cloneDeep } from "lodash-es";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfAdvancedArguments from "@/blocks/RfAdvancedArguments.vue";
import { nextTick, useTemplateRef } from "vue";
import { useActionsStore } from "@/pinia/actionsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { isValidUrlRule as isValidUrlRuleUtil } from "@/utils/triggerFormUtils";

export default {
  name: "RfSetupUsageTrackingModal",
  components: { RfRegexTester, RfTextInput, RfButton, RfSelect, RfAdvancedArguments },
  props: ["showDialog", "CtaTitle", "custom_field", "isUpdating", "disabled"],
  setup: () => ({
    actionsStore: useActionsStore(),
    appsStore: useAppsStore(),
    regexTester: useTemplateRef("regexTester"),
  }),
  data() {
    return {
      pageSettings: cloneDeep(this.custom_field.values[0]) || {}, // should only be used for page trackers
      isTrackerFormValid: false,
    };
  },
  computed: {
    currApp() {
      return this.appsStore.app;
    },
    clientActions() {
      return this.actionsStore.clientActions;
    },
    customTriggers() {
      return this.clientActions
        .filter(action => action.action_type === "custom_trigger")
        .map(action => {
          return { text: action.name, value: action.id };
        });
    },
    isRedfastConnector() {
      return !this.isUpdating || this.custom_field.connector_type === "redfast";
    },
    dataTypes() {
      if (this.isRedfastConnector) {
        return {
          page: "Page",
          track: "Track",
          custom: "Custom",
          advanced: "Advanced",
        };
      }
      return {
        boolean: "Boolean",
        string: "String",
        number: "Number",
        date: "Date",
      };
    },
    canNormalize() {
      if (this.custom_field.display_type === "slider") {
        return true;
      }
      return false;
    },

    testerLabel() {
      return `Test your ${this.testType} expression`;
    },

    testType() {
      return this.custom_field.options.use_regex ? "Regular" : "Wildcard";
    },
    isPageTracker() {
      return this.custom_field.data_type === "page" || this.custom_field.data_type === "advanced";
    },
    regexAllowed() {
      return this.isPageTracker;
    },
    containsRegexOrWildcards() {
      const { url_path, query_params, url_hash } = this.pageSettings;
      const urlStr = [url_path, query_params, url_hash].join();

      return (this.custom_field.options.use_regex ? /[.*+?^${}()|[\]\\]/ : /\*/).test(urlStr);
    },
  },
  watch: {
    "custom_field.data_type": function (to, from) {
      if (from === "page" || from === "advanced") {
        this.custom_field.values = [];
      }
      if (to === "page" || to === "advanced") {
        this.pageSettings = {};
      }
      nextTick(this.validateTrackerForm);
    },
  },
  mounted() {
    this.$refs.advancedArgsBody?.init(this.custom_field.options.custom_trigger_args);
    this.actionsStore.getClientActions(this.currApp.id);
  },
  methods: {
    hasWildCard(string) {
      return string && string.indexOf("*") >= 0;
    },
    processSubmit() {
      if (!this.validateTrackerForm()) return;
      if (this.isPageTracker) {
        this.custom_field.values[0] = this.pageSettings;
      }
      this.$emit("onSubmit", this.$refs.advancedArgsBody?.get());
    },
    validateTrackerForm() {
      this.isTrackerFormValid = this.$refs.trackerPageForm?.validate();
      return this.isTrackerFormValid;
    },
    isValidUrlRule(str) {
      return isValidUrlRuleUtil(str, {
        triggerType: undefined,
        useRegex: this.custom_field.options.use_regex,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  gap: 0px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(200px, 45%), 1fr));
  grid-template-rows: min-content;
}
</style>
